import { Box, HStack } from '@terra/ui-styled-system';
import type {
  ConfirmationMessageType,
  HITLDecision,
  UserConfirmationMessage,
} from '@terra/shared-dto-chat-types';
import {
  ASK_USER_TOOL_NAME,
  ConfirmationMessageTypeEnum,
} from '@terra/shared-dto-chat-types';
import { Text } from '../../../components/styled/text';
import {
  CheckmarkCircle02Icon,
  ArrowDownLeft01Icon,
  Edit03Icon,
  CancelCircleIcon,
} from '@terra/ui-icons';

function readRejectDecisionMessage(decision: HITLDecision | undefined): string {
  if (decision?.type !== 'reject') {
    return '';
  }

  return typeof decision.message === 'string' ? decision.message.trim() : '';
}

export function HandledDataMessageCard({
  messageWithSubs,
}: {
  messageWithSubs: ConfirmationMessageType;
  isNestedInSubAgent?: boolean;
}) {
  if (messageWithSubs.type !== ConfirmationMessageTypeEnum.UserConfirmation) {
    return null;
  }

  const confirmation = messageWithSubs as UserConfirmationMessage;

  if (confirmation.toolName === ASK_USER_TOOL_NAME) {
    return null;
  }

  const decisionText =
    confirmation.decision?.type === 'approve'
      ? 'Approved'
      : confirmation.decision?.type === 'reject'
        ? 'Rejected'
        : confirmation.decision?.type === 'edit'
          ? 'Edited'
          : confirmation.response || 'Responded';
  const replyMessage =
    confirmation.message?.trim() ||
    readRejectDecisionMessage(confirmation.decision);
  const replyText = replyMessage
    ? `${decisionText}: ${replyMessage}`
    : decisionText;
  const isDecisionResponse = Boolean(confirmation.decision);
  const hasReplyMessage = Boolean(replyMessage);

  if (isDecisionResponse) {
    if (
      !hasReplyMessage &&
      (confirmation.decision?.type === 'approve' ||
        confirmation.decision?.type === 'edit' ||
        confirmation.decision?.type === 'reject')
    ) {
      return null;
    }

    const icon =
      confirmation.decision?.type === 'approve' ? (
        <CheckmarkCircle02Icon size={13} />
      ) : confirmation.decision?.type === 'reject' ? (
        <CancelCircleIcon size={13} />
      ) : (
        <Edit03Icon size={13} />
      );
    const iconColor =
      confirmation.decision?.type === 'reject' ? 'fg.error' : 'fg.muted';
    const textColor =
      confirmation.decision?.type === 'reject' ? 'fg.error' : 'fg.muted';

    return (
      <HStack justifyContent="flex-start" width="100%" py={0}>
        <HStack gap={1.5} alignItems="center" maxWidth="100%" color={iconColor}>
          <Box flexShrink={0}>{icon}</Box>
          <Text
            fontSize="xs"
            color={textColor}
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {replyText}
          </Text>
        </HStack>
      </HStack>
    );
  }

  if (!hasReplyMessage && !confirmation.response?.trim()) {
    return null;
  }

  return (
    <HStack justifyContent="flex-end" width="100%" py={0}>
      <HStack
        gap={1.5}
        alignItems="center"
        maxWidth="100%"
        px={2.5}
        py={1}
        borderRadius="8px"
        bg="bg.neutral.muted"
        color="fg.neutral.default"
      >
        <Text
          fontSize="xs"
          fontFamily="mono"
          color="fg.neutral.default"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
        >
          {replyText}
        </Text>
        <Box color="fg.neutral.default" flexShrink={0}>
          <ArrowDownLeft01Icon size={13} />
        </Box>
      </HStack>
    </HStack>
  );
}
